import React from 'react';

import PropTypes from 'prop-types';

import featureNames from '~/shared/constants/featureFlags';
import { ProductTag } from '~/shared/constants/products';
import useAssortmentTestConfigs from '~/shared/utils/useAssortmentTestConfigs';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import {
  getProductAvailabilityForUser,
  ProductUserStatus,
} from '~/techstyle-shared/react-products';

import ProductCardSwatchesDesktop from './ProductCardSwatchesDesktop';
import ProductCardSwatchesMobile from './ProductCardSwatchesMobile';

const DEFAULT_SWATCHES = [];
const DEFAULT_FUNCTION = () => {};

const ProductCardSwatches = ({
  swatches = DEFAULT_SWATCHES,
  activeProductId,
  activeProduct,
  setActiveProductId = DEFAULT_FUNCTION,
  trackProductSwatchClick = DEFAULT_FUNCTION,
}) => {
  const { isMobile } = useBreakpoint();
  const currentUserStatus = useMembership();
  const { enabled: removeOriginFlag } = useHasFeature(
    featureNames.REMOVE_ORIGIN
  );
  const { enabled: isProductsByGroupCodeQueryEnabled } = useHasFeature(
    featureNames.PRODUCTS_BY_GROUP_CODE_QUERY
  );
  const { excludeTagIds } = useAssortmentTestConfigs();

  // TODO: check if this functionality is working
  // TODO: ideally pass membership status to the related products api
  // reference: requiredUserStatus in GridSwatches.js
  // check if membershipLevelGroup has the same effect as checking tagIdList for LEAD_ONLY or VIP_ONLY
  //
  // getGqlRelatedProductDataByGroupCode results already filter based on membership.
  // We can remove this functionality after getGqlRelatedProductDataByGroupCode roll out.
  const excludeTagIdsSet = excludeTagIds?.length
    ? new Set(excludeTagIds)
    : null;

  const membershipBasedSwatches = isProductsByGroupCodeQueryEnabled
    ? swatches
    : swatches.filter(({ tagIdList }) => {
        let requiredUserStatus = [];
        if (!tagIdList?.length) {
          return getProductAvailabilityForUser({
            currentUserStatus,
            requiredUserStatus,
          });
        }

        for (const tag of tagIdList) {
          if (removeOriginFlag && tag === ProductTag.removeOrigin) {
            return false;
          }
          if (excludeTagIdsSet?.has(tag)) {
            return false;
          }
          if (tag === ProductTag.leadOnly) {
            requiredUserStatus = [ProductUserStatus.LEAD_ONLY];
          } else if (
            tag === ProductTag.vipOnly &&
            requiredUserStatus.length === 0
          ) {
            requiredUserStatus = [ProductUserStatus.VIP_ONLY];
          }
        }

        return getProductAvailabilityForUser({
          currentUserStatus,
          requiredUserStatus,
        });
      });

  return isMobile ? (
    <ProductCardSwatchesMobile
      swatches={membershipBasedSwatches}
      activeProduct={activeProduct}
      activeProductId={activeProductId}
      setActiveProductId={setActiveProductId}
      trackProductSwatchClick={trackProductSwatchClick}
    />
  ) : (
    <ProductCardSwatchesDesktop
      swatches={membershipBasedSwatches}
      activeProduct={activeProduct}
      activeProductId={activeProductId}
      setActiveProductId={setActiveProductId}
      trackProductSwatchClick={trackProductSwatchClick}
    />
  );
};

ProductCardSwatches.propTypes = {
  activeProduct: PropTypes.object,
  activeProductId: PropTypes.number,
  setActiveProductId: PropTypes.func,
  swatches: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
      label: PropTypes.string,
    })
  ),
  trackProductSwatchClick: PropTypes.func,
};

export default ProductCardSwatches;
